import React, { createContext, useContext, useState, useCallback } from "react";
import { UnifiedAlert, AlertType } from "@/components/ui/unified-alert";

export interface AlertOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
}

export interface PromptOptions extends AlertOptions {
  defaultValue?: string;
}

interface AlertState {
  open: boolean;
  type: AlertType;
  title?: string;
  message: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
  resolve?: (value: any) => void;
  reject?: () => void;
}

interface AlertContextType {
  alert: (message: string | AlertOptions) => Promise<void>;
  confirm: (message: string | AlertOptions) => Promise<boolean>;
  prompt: (message: string | PromptOptions) => Promise<string | null>;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: React.ReactNode }) {
  const [alertState, setAlertState] = useState<AlertState | null>(null);

  const showAlert = useCallback(
    (state: Omit<AlertState, "open">): Promise<any> => {
      return new Promise((resolve, reject) => {
        setAlertState({
          ...state,
          open: true,
          resolve,
          reject,
        });
      });
    },
    []
  );

  const handleConfirm = useCallback(
    (value?: string) => {
      if (alertState) {
        if (alertState.type === "prompt") {
          alertState.resolve?.(value || "");
        } else if (alertState.type === "confirm") {
          alertState.resolve?.(true);
        } else {
          alertState.resolve?.(undefined);
        }
        setAlertState(null);
      }
    },
    [alertState]
  );

  const handleCancel = useCallback(() => {
    if (alertState) {
      if (alertState.type === "confirm") {
        alertState.resolve?.(false);
      } else if (alertState.type === "prompt") {
        alertState.resolve?.(null);
      } else {
        alertState.resolve?.(undefined);
      }
      setAlertState(null);
    }
  }, [alertState]);

  const alert = useCallback(
    async (messageOrOptions: string | AlertOptions): Promise<void> => {
      const options =
        typeof messageOrOptions === "string"
          ? { message: messageOrOptions }
          : messageOrOptions;
      await showAlert({
        type: "alert",
        message: options.message,
        title: options.title,
        confirmText: options.confirmText,
      });
    },
    [showAlert]
  );

  const confirm = useCallback(
    async (messageOrOptions: string | AlertOptions): Promise<boolean> => {
      const options =
        typeof messageOrOptions === "string"
          ? { message: messageOrOptions }
          : messageOrOptions;
      return (await showAlert({
        type: "confirm",
        message: options.message,
        title: options.title,
        confirmText: options.confirmText,
        cancelText: options.cancelText,
        variant: options.variant || "default",
      })) as boolean;
    },
    [showAlert]
  );

  const prompt = useCallback(
    async (messageOrOptions: string | PromptOptions): Promise<string | null> => {
      const options =
        typeof messageOrOptions === "string"
          ? { message: messageOrOptions }
          : messageOrOptions;
      return (await showAlert({
        type: "prompt",
        message: options.message,
        title: options.title,
        defaultValue: options.defaultValue || "",
        confirmText: options.confirmText,
        cancelText: options.cancelText,
      })) as string | null;
    },
    [showAlert]
  );

  return (
    <AlertContext.Provider value={{ alert, confirm, prompt }}>
      {children}
      {alertState && (
        <UnifiedAlert
          open={alertState.open}
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          defaultValue={alertState.defaultValue}
          confirmText={alertState.confirmText}
          cancelText={alertState.cancelText}
          variant={alertState.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </AlertContext.Provider>
  );
}

export function useAlert(): AlertContextType {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
}

